const AMMUNITION_NAME_PATTERN = /\b(?:arrows?|crossbow bolts?|sling bullets?|blowgun needles?)\b/i;

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const normalizeBooleanValue = (value) => {
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim().toLowerCase();
  if (['true', 'yes', 'y', '1', 'required', 'requires attunement', 'attuned', 'equipped'].includes(normalized)) {
    return true;
  }
  if (['false', 'no', 'n', '0', 'none', 'not required', 'not attuned', 'unequipped'].includes(normalized)) {
    return false;
  }
  return undefined;
};

const normalizeEquipmentType = (value, fallback = 'item') => {
  const normalized = String(value || fallback).trim().toLowerCase();
  if (/\bshield\b/.test(normalized)) return 'shield';
  if (/\barmor\b|\barmour\b/.test(normalized)) return 'armor';
  if (/\bweapon\b/.test(normalized)) return 'weapon';
  if (/\bammunition\b|\bammo\b/.test(normalized)) return 'ammunition';
  if (/\bconsumable\b|\bpotion\b|\bscroll\b/.test(normalized)) return 'consumable';
  if (/\btool\b|\bkit\b/.test(normalized)) return 'tool';
  if (/\bvaluable\b|\bgem\b|\bjewel\b/.test(normalized)) return 'valuable';
  return ['item', 'weapon', 'armor', 'shield', 'consumable', 'tool', 'ammunition', 'valuable'].includes(normalized)
    ? normalized
    : 'item';
};

const normalizeQuantity = (value) => {
  if (value === undefined || value === null || value === '') return 1;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
};

export const equipmentEntryName = (entry) => String(
  typeof entry === 'string' ? entry : (entry?.name || entry?.title || entry?.label || '')
).trim();

export const isAmmunitionEntry = (entry) => {
  const record = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry : {};
  const typeLabel = String(
    record.type
    || record.kind
    || record.category
    || record.itemType
    || record.entryType
    || ''
  ).trim();
  return record.ammunition === true
    || /\b(?:ammunition|ammo)\b/i.test(typeLabel)
    || AMMUNITION_NAME_PATTERN.test(equipmentEntryName(entry));
};

// date_of_change: 2026-07-29
// rule_reference: Character-sheet inventory uses explicit structured item metadata to separate
// magic items from mundane gear without parsing rules text or item names.
export const isMagicEquipmentEntry = (entry) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
  const typeLabel = String(
    entry.type
    || entry.kind
    || entry.category
    || entry.itemType
    || entry.entryType
    || ''
  ).trim();

  return entry.magical === true
    || entry.isMagic === true
    || entry.magic === true
    || Boolean(String(entry.rarity || '').trim())
    || entry.attunement === true
    || entry.requiresAttunement === true
    || entry.attuned === true
    || /\bmagic(?:al)?(?:\s+item)?\b/i.test(typeLabel);
};

// date_of_change: 2026-07-29
// rule_reference: SmartPaste equipment imports must preserve the same canonical inventory state
// used by direct character-sheet editing, including explicit equipped and attunement choices.
export const normalizeEquipmentInventoryEntry = (entry, defaults = {}) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return entry;

  const rarity = String(firstDefined(entry.rarity, entry.itemRarity, entry.item_rarity, defaults.rarity) || '')
    .trim()
    .toLowerCase();
  const attunedSignal = normalizeBooleanValue(firstDefined(
    entry.attuned,
    entry.isAttuned,
    entry.is_attuned,
    defaults.attuned
  ));
  const requiresSignal = normalizeBooleanValue(firstDefined(
    entry.requiresAttunement,
    entry.requires_attunement,
    entry.attunementRequired,
    entry.attunement_required,
    entry.attunement,
    defaults.requiresAttunement,
    defaults.attunement
  ));
  const equippedSignal = normalizeBooleanValue(firstDefined(
    entry.equipped,
    entry.isEquipped,
    entry.is_equipped,
    entry.worn,
    entry.wielded,
    defaults.equipped
  ));
  const magicalSignal = normalizeBooleanValue(firstDefined(
    entry.magical,
    entry.isMagic,
    entry.is_magic,
    entry.magic,
    entry.enchanted,
    defaults.magical,
    defaults.isMagic
  ));
  const attuned = attunedSignal === true;
  const requiresAttunement = attuned || requiresSignal === true;
  const magical = magicalSignal === true || Boolean(rarity) || requiresAttunement || attuned;
  const descriptionFallback = firstDefined(
    entry.description,
    entry.rulesText,
    entry.rules_text,
    entry.text,
    entry.details,
    entry.notes,
    defaults.description
  );
  const propertyDescription = Array.isArray(entry.properties)
    ? entry.properties.filter(Boolean).join('\n')
    : '';
  const effectDescription = Array.isArray(entry.effects)
    ? entry.effects
      .map((effect) => typeof effect === 'string' ? effect : effect?.description)
      .filter(Boolean)
      .join('\n')
    : '';

  return {
    ...entry,
    name: equipmentEntryName(entry) || equipmentEntryName(defaults),
    type: normalizeEquipmentType(
      firstDefined(entry.type, entry.kind, entry.category, entry.itemType, entry.item_type, defaults.type),
      defaults.type
    ),
    description: String(descriptionFallback || propertyDescription || effectDescription || ''),
    quantity: normalizeQuantity(firstDefined(entry.quantity, entry.qty, entry.count, defaults.quantity)),
    weight: firstDefined(entry.weight, entry.weight_lbs, entry.encumbrance, defaults.weight, ''),
    rarity,
    magical,
    attunement: requiresAttunement,
    requiresAttunement,
    attuned,
    equipped: equippedSignal === true
  };
};

export const isExplicitlyEquippedWeapon = (entry, sourceCollection = '') => {
  if (!entry || isAmmunitionEntry(entry)) return false;
  const explicitCollection = /(?:^|\.)(?:weapons|equippedWeapons)$/.test(String(sourceCollection));
  return explicitCollection || entry?.equipped === true;
};
